import { useQuery } from '@tanstack/react-query';
import compact from 'lodash/compact';

import { FilterBarChip } from '@/components/shared/FilterBar/types';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useAuthorizedUnits from '@/data/hook/shared/useAuthorizedUnits';
import { ScopeFilters } from '@/data/hook/shared/useScopeFilters';
import useAuth from '@/data/hook/useAuth';
import { unitsQueryKeys, usersQueryKeys } from '@/data/services/querykeys';
import { isAdmin } from '@/functions/auth';
import { handleUserFullName } from '@/functions/handleUserFullName';

export default function useScopeFilterChips({
  unit,
  teacher,
}: ScopeFilters): FilterBarChip<keyof ScopeFilters>[] {
  const { user } = useAuth();
  const isAdminUser = isAdmin(user?.userType);
  const { canFilterByUnit } = useAuthorizedUnits();

  const { data: selectedUnit, isInitialLoading: isLoadingUnit } = useQuery({
    ...unitsQueryKeys.get(unit ?? 0),
    enabled: canFilterByUnit && !!unit,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { data: selectedTeacher, isInitialLoading: isLoadingTeacher } =
    useQuery({
      ...usersQueryKeys.get(teacher ?? 0),
      enabled: isAdminUser && !!teacher,
      staleTime: REQUEST_STALE_TIME_IN_MS,
    });

  return compact<FilterBarChip<keyof ScopeFilters>>([
    (selectedUnit || isLoadingUnit) && {
      key: 'unit',
      label: selectedUnit?.name ?? '',
      isLoading: isLoadingUnit,
    },
    (selectedTeacher || isLoadingTeacher) && {
      key: 'teacher',
      label: selectedTeacher ? handleUserFullName(selectedTeacher) : '',
      isLoading: isLoadingTeacher,
    },
  ]);
}
